<!--
  Every document this principal can see, arranged the way they filed them --
  as a FILE BROWSER rather than a list with a folder rail beside it.

  TASK-042 phases 1-3, plus the performance requirement that came after them.
  Five things about this screen are load-bearing:

  1. **The role comes from the server, per row.** `GET /v1/docs` returns it,
     computed by the same expression `assertDocAccess` enforces, and the action
     set is built from it by `docActions()`. Inferring "this must be mine
     because I can see it" is how a list starts offering actions the API
     refuses.

  2. **Deleting is recoverable and says so.** Archiving hides a document from
     every listing including its owner's, so the confirm names where it goes
     and the rail has a Deleted view that brings it back.

  3. **Every action is on the RIGHT.** The rail is navigation only (§2); rename,
     delete and create live in the toolbar beside the folder's name (§3a), and
     a document's actions are buttons at the right of its row (§3b). This is a
     correction to TASK-039 phase 1, not a new idea.

  4. **The view lives in the URL, not in a local `$state`.** `/?folder=<id>` is
     what makes a folder linkable, bookmarkable and reachable with Back, which
     is the actual difference between a file browser and a filtered list. The
     view is DERIVED from `router.query`; selecting one is a `router.go()`.

  5. **A click repaints from cache, and the network happens underneath.** The
     breadcrumb, the subfolder rows and the toolbar are derived from
     `folderStore.folders`, which is loaded once and held in memory -- so
     navigating cannot need a request to redraw them. Only the DOCUMENT list is
     fetched, and it never clears: a visited place repaints on the click's own
     frame and its rows are replaced when the response lands. The only pending
     affordance is a 2px rule that occupies its space whether or not anything
     is loading, so nothing on this screen moves while it waits.

     The corollary, and the reason `act()` is gone: every mutation is applied to
     the store FIRST and reconciled afterwards. `act()` reloaded both panes on
     success, which would land in the middle of the optimistic state and
     overwrite it (§6d). `commit()` below is `act()` with the reload taken out
     and the revert moved into the store, where the snapshot is.

  6. **A document is dragged into a folder, and nothing is drag-only.** A row
     is `draggable`; a folder row, a breadcrumb ancestor and the tree accept
     the drop (§6a, §6b). The gesture is a shortcut for the Move to… dialog and
     never the only way: HTML5 drag does not exist on touch and does not exist
     for a keyboard, so **Move to… stays in every row's primary action set**
     and is the path every test of the move takes. The parts that decide
     anything -- the capacity arithmetic, the refusal-to-sentence mapping, the
     revert and the queue -- are in `lib/drop.ts` and are unit-tested there.

  The list `<section>` is deliberately NOT `aria-live`. It used to be, which
  meant every row, breadcrumb, skeleton and empty state was announced, and the
  two messages that were meant to be announced -- `notice` and `actionError` --
  were announced twice inside it. Announcing is something this screen does on
  purpose, in named regions (§6c). The drag gets a THIRD such region,
  `dragNotice`, rather than borrowing `notice`: `select()` clears `notice` on
  every view change and a drag frequently ends in one, so a drag announcement
  parked there would be wiped by the navigation the drag caused.
-->
<script lang="ts">
  import Icon from './../lib/components/Icon.svelte'
  import { tick, untrack } from 'svelte'
  import { ApiError, describeError, isAuthError, isMissingError } from '../lib/api'
  import { redirectToLogin } from '../lib/login'
  import { navigate, router } from '../lib/router.svelte'
  import { Reverted, folderStore } from '../lib/folderstore.svelte'
  import TopBar from '../lib/components/TopBar.svelte'
  import AuthErrorNotice from '../lib/components/AuthErrorNotice.svelte'
  import FolderRail from '../lib/components/FolderRail.svelte'
  import FolderToolbar from '../lib/components/FolderToolbar.svelte'
  import DocRowActions from '../lib/components/DocRowActions.svelte'
  import DocTile from '../lib/components/DocTile.svelte'
  import ViewSwitch from '../lib/components/ViewSwitch.svelte'
  import RailDivider from '../lib/components/RailDivider.svelte'
  import ModalSheet from '../lib/components/ModalSheet.svelte'
  import ShareDialog from '../lib/components/ShareDialog.svelte'
  import { myUserId, readStored, scopedKey, writeStored } from '../lib/me'
  import {
    DEFAULT_SORT,
    ariaSortOf,
    browserOrder,
    formatSort,
    nextSort,
    parseSort,
    type Sort,
    type SortKey,
  } from '../lib/doclist-sort'
  import type { DocRow, Folder, PaneView, View } from '../lib/components/doclist-types'
  import {
    ROOT_GLYPH,
    ROOT_NAME,
    buildFolderTree,
    childFolders,
    flattenTree,
    viewFromQuery,
    viewHref,
    viewPath,
  } from '../lib/components/doclist-types'
  import type { DropTarget, DropZone } from '../lib/drop'
  import {
    DRAG_MIME,
    TREE_ACCEPTS_DROPS,
    acceptDrag,
    canDrop,
    docIdsFrom,
    encodeDrag,
    leftTarget,
    refusalMessage,
    revertSentence,
    serialise,
  } from '../lib/drop'

  let busy = $state(false)
  /** Above the list: something about the last action, not about the page. */
  let notice = $state('')
  let actionError = $state('')
  /**
   * The drag's own region (§6c). It is a THIRD `role="status"` node and not a
   * reuse of `notice`, because `select()` clears `notice` on every view change
   * and a drag frequently ends in one -- so a drag announcement parked there
   * would be wiped by the navigation the drag caused. It is cleared when the
   * drag ends, so it never outlives the gesture it describes.
   */
  let dragNotice = $state('')
  /** Signed in, but this account can see nothing. The one dead end left. */
  let stuck = $state('')

  /**
   * The view IS the URL. Nothing assigns to it (§1a).
   *
   * `router.query` is republished on `popstate`, so Back and Forward walk the
   * folders the person walked without this component knowing they exist.
   */
  const view = $derived<View>(viewFromQuery(router.query))

  type Dialog =
    | { kind: 'share'; doc: DocRow }
    | { kind: 'leave'; doc: DocRow }
    | { kind: 'rename'; doc: DocRow; name: string }
    | { kind: 'move'; doc: DocRow; folderId: string | null }
    | { kind: 'delete'; doc: DocRow }
    | { kind: 'folder-delete'; folder: Folder }
    | { kind: 'folder-rename'; folder: Folder; name: string }
    | { kind: 'folder-create'; parentId: string | null; name: string }
  let dialog = $state<Dialog | null>(null)
  let dialogError = $state('')

  /* --------------------------------------------------------- from the cache */

  /**
   * Everything about a PLACE comes from the one tree in memory, synchronously.
   * None of these four waits for anything.
   */
  const folders = $derived(folderStore.folders)
  const archived = $derived(view.kind === 'archived')
  /**
   * The breadcrumb, always rendered and always rooted (§1b). Every crumb but
   * the last selects, including Root, which is how a person gets back to the
   * top without touching the tree.
   */
  const crumbs = $derived(viewPath(view, folders))

  /*
   * At the root both lines say the same thing -- the path IS `/` and so is the
   * heading -- which reads as a stutter and says nothing the tree does not
   * already show. So the whole block is blanked there, not removed: it keeps
   * its space (`visibility: hidden`, which also takes it out of the
   * accessibility tree) so the table does not jump up by two lines when you
   * step from a folder to the root and back.
   */
  const blankWhere = $derived(view.kind === 'root')
  const here = $derived(crumbs[crumbs.length - 1])
  /** The move dialog's list: tree order, indented, one line each. */
  const movable = $derived(flattenTree(buildFolderTree(folders)))
  /** The folder this pane is showing, if it is showing one. */
  const current = $derived(
    view.kind === 'folder' ? folders.find((f) => f.id === view.id) : undefined,
  )
  /**
   * The subfolders of the place on screen. Taken from the flat
   * `GET /v1/folders` the rail already holds rather than from the children
   * response, so the pane and the rail cannot disagree about what is in a
   * folder.
   */
  const paneFolders = $derived(
    view.kind === 'folder'
      ? childFolders(folders, view.id)
      : view.kind === 'root'
        ? childFolders(folders, null)
        : [],
  )
  const isPlace = $derived(view.kind === 'folder' || view.kind === 'root')

  /** The document list of the place on screen, as far as this tab knows it. */
  const entry = $derived(folderStore.entry(view))
  const docs = $derived(entry?.rows ?? [])
  /** A request is out for this view. Nothing is cleared while it is. */
  const pendingDocs = $derived(entry?.pending ?? false)
  /**
   * This tab has never had an answer for this place. It is the only case with
   * nothing to repaint, and it draws placeholder rows rather than a spinner --
   * and `prefetch()` on hover means it is usually over before the click.
   */
  const unseen = $derived(!entry || entry.at === 0)
  const loadError = $derived(entry && !entry.pending && entry.error && docs.length === 0 ? entry.error : '')

  /* ------------------------------------------------------- list or icons */

  /*
   * TASK-042 §5a. Per PERSON, keyed by user id -- which is a different thing
   * from one unkeyed entry, and the two give different answers the moment two
   * people share a browser profile. The default is drawn while `me()` is in
   * flight and the stored value is applied when it answers: a switch that
   * arrives one frame late is cheaper than a screen that waits for it.
   *
   * Every access is through `lib/me.ts`, which wraps the `localStorage` calls
   * in try/catch -- a blocked origin must not take the screen down for a
   * preference.
   */
  const VIEW_KEY = 'unomena.docs.view'
  /** One more key, the same scheme: which column the list is ordered by. */
  const SORT_KEY = 'unomena.docs.sort'

  let pane = $state<PaneView>('list')
  let paneKey = $state('')
  let sort = $state<Sort>(DEFAULT_SORT)
  let sortKey = $state('')

  $effect(() => {
    void (async () => {
      const userId = await myUserId()
      if (!userId) return
      const key = scopedKey(VIEW_KEY, userId)
      paneKey = key
      const stored = readStored(key)
      if (stored === 'icons' || stored === 'list') pane = stored
      // Both preferences are the same shape and the same scope, so they are
      // read in the same place off the same answer: one `GET /v1/auth/me`,
      // two keys. `parseSort` treats anything it does not recognise as the
      // default, so a hand-edited or stale entry is a shrug rather than a
      // broken list.
      const skey = scopedKey(SORT_KEY, userId)
      sortKey = skey
      sort = parseSort(readStored(skey))
    })()
  })

  function setPane(next: PaneView) {
    pane = next
    // Only once we know whose preference it is. Before that it is not written
    // at all, rather than written somewhere it would have to be migrated from.
    if (paneKey) writeStored(paneKey, next)
  }

  /** A header click. `nextSort` owns "same column reverses, new column resets". */
  function applySort(key: SortKey) {
    sort = nextSort(sort, key)
    if (sortKey) writeStored(sortKey, formatSort(sort))
  }

  /**
   * The visible half of the direction. `aria-sort` on the `<th>` is the half
   * that is announced.
   *
   * Every header draws one, and the inactive ones are drawn at zero opacity
   * rather than left out, so moving the sort cannot change a header cell's
   * width and shuffle the row underneath it.
   */
  function arrow(key: SortKey): string {
    return sort.key === key && sort.dir === 'desc' ? '▾' : '▴'
  }

  /** Icons only where there is a grid to draw: Trash and All documents too. */
  const icons = $derived(pane === 'icons')

  /*
   * The grid's column count, measured rather than assumed.
   *
   * `role="grid"` needs a `role="row"` per VISUAL row, which means the markup
   * has to know how many tiles fit -- and so does the arrow-key handler, since
   * Down is "one row further on" and a row is however many columns there are.
   * One measured width, one division, and the CSS uses the same number, so the
   * two cannot disagree.
   */
  const TILE_MIN = 176
  const TILE_GAP = 12
  let gridWidth = $state(0)
  const columns = $derived(
    Math.max(1, Math.floor((gridWidth + TILE_GAP) / (TILE_MIN + TILE_GAP))) || 1,
  )
  const tileRows = $derived.by(() => {
    const out: DocRow[][] = []
    for (let i = 0; i < docs.length; i += columns) out.push(docs.slice(i, i + columns))
    return out
  })
  /** The one tile carrying `tabindex="0"`, so Tab enters the grid once. */
  let activeTile = $state(0)

  $effect(() => {
    // A folder change, or a document leaving, must not leave the tab stop
    // pointing past the end of the grid.
    void docs.length
    if (activeTile > docs.length - 1) activeTile = 0
  })

  /**
   * Two dimensions, because a wrapped grid has no reading order a linear walk
   * can recover (§9). Left and Right walk the flat order -- which is what a
   * person means by "the next one" -- and Up and Down move a whole row.
   */
  function tileKey(event: KeyboardEvent, doc: DocRow) {
    const i = docs.findIndex((d) => d.id === doc.id)
    if (i < 0) return
    let next = i
    switch (event.key) {
      case 'ArrowRight':
        next = Math.min(i + 1, docs.length - 1)
        break
      case 'ArrowLeft':
        next = Math.max(i - 1, 0)
        break
      case 'ArrowDown':
        next = Math.min(i + columns, docs.length - 1)
        break
      case 'ArrowUp':
        next = Math.max(i - columns, 0)
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = docs.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    activeTile = next
    const id = docs[next]?.id
    if (id) {
      document.querySelector<HTMLElement>(`[data-tile-id="${CSS.escape(id)}"]`)?.focus()
    }
  }

  /* ------------------------------------------------------------- the rail */

  /*
   * TASK-042 §4. The width is DEVICE-scoped and therefore unkeyed: a rail
   * width is a property of a screen, the way a window size is, and one that
   * followed a person from a laptop to a 27-inch monitor would be the bug.
   * Nothing stored here names a document.
   */
  const RAIL_KEY = 'unomena.docs.railWidth'
  const RAIL_DEFAULT = 260
  const RAIL_MIN = 180
  /** The one place the two breakpoints are written. See the note on `.layout`. */
  const WIDE = 900
  const NARROW = 640

  let viewportWidth = $state(typeof window === 'undefined' ? 1280 : window.innerWidth)
  let railWidth = $state(readRailWidth())
  let railOpen = $state(false)

  const railMax = $derived(Math.max(RAIL_MIN, Math.min(480, Math.round(viewportWidth * 0.4))))
  const railPx = $derived(Math.min(Math.max(railWidth, RAIL_MIN), railMax))
  const stacked = $derived(viewportWidth < NARROW)
  /** Below 900px there is not enough width left to give any of it away. */
  const resizable = $derived(viewportWidth >= WIDE)
  const railTrack = $derived(resizable ? railPx : RAIL_DEFAULT)

  function readRailWidth(): number {
    try {
      const raw = localStorage.getItem(RAIL_KEY)
      const n = raw === null ? Number.NaN : Number(raw)
      return Number.isFinite(n) && n > 0 ? n : RAIL_DEFAULT
    } catch {
      // Private mode, or storage is blocked. A column width is not worth a
      // blank screen: fall back to the default and carry on.
      return RAIL_DEFAULT
    }
  }

  /** Called once a gesture ENDS, never per frame. */
  function persistRail() {
    try {
      localStorage.setItem(RAIL_KEY, String(railPx))
    } catch {
      /* see readRailWidth: the width simply does not survive the reload */
    }
  }

  /* ------------------------------------------------- the columns, and when */

  /*
   * Which columns the row has room for.
   *
   * Derived from the SAME `viewportWidth` and the SAME two constants the rail
   * uses, deliberately: a fourth set of thresholds -- a media query in this
   * file, another in `DocRowActions`, `WIDE`/`NARROW` here -- is how a screen
   * ends up with a band of widths where one rule says the rail is a disclosure
   * and another says the row still has five columns.
   *
   * `TIGHT` is the one new number, and it is the width at which Name plus its
   * actions is all that is left. Columns are DROPPED rather than squeezed,
   * because a date squeezed to "19 Se…" is worse than no date at all.
   */
  const TIGHT = 520
  const showBy = $derived(viewportWidth >= WIDE)
  const showRev = $derived(viewportWidth >= NARROW)
  const showWhen = $derived(viewportWidth >= TIGHT)

  /**
   * The rows of the list view, in the order they are drawn.
   *
   * Folders first as a GROUP, always -- the reason is in `doclist-sort.ts` and
   * it is the file-browser convention, not an oversight to be tidied away.
   * The icon view is NOT sorted from here: it keeps the response order it has
   * always had, because the tile grid is out of scope for this work.
   */
  const order = $derived(browserOrder(paneFolders, docs, sort, { archived }))
  const hasRows = $derived(order.folders.length > 0 || order.docs.length > 0)

  /*
   * A place with nothing in it. It draws as the LIST it is -- headers, and one
   * centred row saying so -- rather than as a page of advice: an empty folder
   * is an ordinary state of a file browser, and the two sentences of
   * instructions that used to stand here made it look like a problem. The
   * gestures they described are on the document's own row menu anyway.
   *
   * The root is left out: an empty root is the first-run screen, where the
   * only useful thing to say is how to get a document at all.
   */
  const emptyPlace = $derived(
    view.kind === 'folder' && !loadError && order.folders.length === 0 && order.docs.length === 0,
  )

  /** Columns in the browser table, for the empty row's `colspan`. */
  const fbColumns = $derived(1 + (showWhen ? 1 : 0) + (showRev ? 1 : 0) + (showBy ? 1 : 0) + 1)


  /**
   * The date column: SHORT and ABSOLUTE -- "19 Sep 2026, 00:00" -- with the
   * full value in a `title`.
   *
   * The alternative was a relative form ("2 hours ago"), and it lost on three
   * counts. It goes stale in a list that sits open while its rows are being
   * written; it is a different width on every row, so the column cannot be
   * narrow and nothing can be scanned down; and the common case here is
   * several documents touched the same afternoon, where "2 hours ago" against
   * "3 hours ago" is harder to read than 14:05 against 11:20. Absolute is also
   * the only one of the two that can be compared against the sort the header
   * claims to have applied.
   *
   * One formatter, made once: `Intl.DateTimeFormat` is expensive to construct
   * and this runs on every row of a hundred.
   */
  const SHORT_WHEN = new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  function shortWhen(iso: string | null | undefined): string {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : SHORT_WHEN.format(d)
  }

  /* ------------------------------------------------------------ the loads */

  /**
   * The one place a failure becomes something a person can read.
   *
   * A 401, or a 403 whose refusal path is about the credential rather than a
   * document, means sign in again -- `isAuthError` already knows the
   * difference. Everything else is one sentence, never the refusal JSON.
   */
  function report(err: unknown, prefix: string): string | null {
    if (isAuthError(err)) {
      if (redirectToLogin()) return null
      // Only reachable when the bounce guard has given up, which now means a
      // session that /v1/auth/me accepts and this route does not -- not "you
      // are signed in and have nothing", which is what it used to claim.
      stuck = 'This session cannot open your documents. Sign in again to carry on.'
      return null
    }
    return `${prefix}: ${describeError(err)}.`
  }

  /**
   * A bookmark to a folder that has been deleted, or a `?folder=` somebody
   * hand-edited, is an ordinary event in a file browser rather than an error
   * screen.
   *
   * Two specifics. `isMissingError` and not "any failure": a 500 or a dead
   * network must not quietly drop somebody at the root as though their folder
   * were gone. And `dropQuery` -- a replaceState -- and not `router.go('/')`:
   * `go` pushes, so the dead link would stay one step back and Back would
   * return the person to it, refuse again, and bounce them here again.
   */
  function loadFailed(asked: View, err: unknown) {
    const unreadable = err instanceof ApiError && err.status === 400
    if (asked.kind === 'folder' && (isMissingError(err) || unreadable)) {
      notice = `That folder is gone. Showing ${ROOT_NAME} instead.`
      actionError = ''
      router.dropQuery('folder')
      return
    }
    const message = report(err, 'Could not load documents')
    if (message) actionError = message
  }

  // No local signed-out check. The session lives in an HttpOnly cookie this
  // script cannot see, so the only honest way to ask whether we are signed in
  // is to make the request and let a 401 answer it.
  //
  // `start()` is idempotent and `watch()` is what revalidates the tree when the
  // person comes back to the tab -- the cheap half of TASK-042's invalidation
  // (see the seam note in `folderstore.svelte.ts`).
  //
  // `untrack`, in both effects below, is load-bearing rather than tidy: the
  // store methods read the very state they are about to write, so a tracked
  // call would make each effect a dependency of its own result and re-run it
  // on every response. Each of these depends on ONE thing -- the first on
  // nothing, the second on the URL -- and that is what it should depend on.
  $effect(() => {
    untrack(() => folderStore.start())
    return folderStore.watch()
  })

  // Re-runs whenever the URL's view changes -- including on Back, because
  // `router.query` is republished on `popstate`. It does NOT clear anything:
  // the pane keeps what it has until this resolves.
  $effect(() => {
    const asked = view
    untrack(() => folderStore.ensureDocs(asked).catch((err) => loadFailed(asked, err)))
  })

  /* ---------------------------------------------------------- navigation */

  function select(next: View) {
    notice = ''
    actionError = ''
    railOpen = false
    router.go(viewHref(next))
  }

  /**
   * A crumb and a folder row are real links, so they can be copied, opened in
   * a new tab and followed by the back button. This is the same guard
   * `navigate()` uses; it exists separately only because selecting also clears
   * the two messages.
   */
  function follow(event: MouseEvent, next: View) {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    select(next)
  }

  function viewOf(crumb: { id: string | null }): View {
    return crumb.id ? { kind: 'folder', id: crumb.id } : { kind: 'root' }
  }

  /**
   * Pointing at a folder is a reliable signal that it is about to be clicked,
   * and one bounded request spent on a guess is what makes the FIRST visit to
   * a place feel like the second one.
   */
  function warm(next: View) {
    folderStore.prefetch(next)
  }

  /* ---------------------------------------------------- drag and drop */

  /*
   * TASK-042 §6. A document is dragged onto a place; the place is a folder row
   * in this pane, a breadcrumb ancestor, or a folder in the tree (§6b).
   *
   * Three rules this code exists to keep, all of them from the design review:
   *
   *  - **Nothing is drag-only.** HTML5 drag does not work on touch and does
   *    not work for a keyboard, so Move to… stays in every row's primary
   *    action set and does the same thing through `folderStore.moveDoc`. This
   *    is a shortcut, never the only path.
   *  - **The pre-check counts `docCount + subfolders`** -- see `lib/drop.ts`.
   *    It is a courtesy and never a fence: it saves a round trip and lets a
   *    target say no during the drag, and the server remains the authority.
   *  - **A refused drop reverts OUT LOUD.** The store's snapshot puts the row
   *    back at its remembered index and restores the counts; this puts a
   *    sentence naming what came back, where, and why in `actionError`, and
   *    moves focus to the row that came back. A silent snap-back is the one
   *    outcome this must never have.
   */

  /** The document being dragged, by id. `null` at rest -- nothing is drawn. */
  let dragDocId = $state<string | null>(null)
  /** Which drop target the pointer is over, for the highlight in THIS pane. */
  let overId = $state<string | null | undefined>(undefined)
  const draggingDoc = $derived(dragDocId ? (docs.find((d) => d.id === dragDocId) ?? null) : null)

  /** Drops run one at a time (§6d step 2). See `serialise()` for why. */
  const runDrop = serialise()

  /**
   * A place, with both halves of what the server counts in it.
   *
   * `docCount` is this person's placements; the subfolders come from the same
   * flat tree the rail draws from. The root is a place without a row and has
   * no cap at all -- `canDrop` says why.
   */
  function targetFor(id: string | null): DropTarget {
    if (id === null) return { id: null, name: ROOT_NAME, docCount: 0, subfolders: 0 }
    const folder = folders.find((f) => f.id === id)
    return {
      id,
      name: folder?.name ?? 'that folder',
      docCount: folder?.docCount ?? 0,
      subfolders: folders.filter((f) => f.parentId === id).length,
    }
  }

  function verdictFor(id: string | null) {
    const doc = draggingDoc
    if (!doc) return { ok: true } as const
    return canDrop(targetFor(id), doc.folderId ?? null, doc.name)
  }

  function startDocDrag(event: DragEvent, doc: DocRow) {
    if (!event.dataTransfer) return
    // `{ids: […]}` from day one, on a custom MIME type: an array because
    // multi-select (§6e) must not reshape the protocol, and a custom type
    // because a target has to decide whether to accept before it is allowed to
    // read the data.
    event.dataTransfer.setData(DRAG_MIME, encodeDrag([doc.id]))
    event.dataTransfer.effectAllowed = 'move'
    dragDocId = doc.id
    dragNotice = `Moving “${doc.name}”. Drop on a folder.`

    /*
     * BUG-022. Nothing here may move the list, in this frame or the next.
     *
     * Chromium cancels a drag whose source has moved out from under the drag
     * origin during `dragstart`, and fires `dragend` at once. Clearing these
     * two removed a message line above the table -- 55.6px, more than a whole
     * row -- so the FIRST drag in a folder with a notice showing died on the
     * spot. `dragNotice` itself is safe: it renders into a fixed strip (see
     * `.drag-toast`) that is outside the list's flow entirely, which is also
     * what stops the rows shuffling under the pointer mid-gesture.
     *
     * The clearing is deferred a frame rather than dropped: the messages are
     * stale the moment a new gesture starts, and by the next frame the drag is
     * under way and the reflow cannot cancel it.
     */
    requestAnimationFrame(() => {
      notice = ''
      actionError = ''
    })
  }

  function endDocDrag() {
    dragDocId = null
    overId = undefined
    dragNotice = ''
  }

  /**
   * Pointed at during a drag.
   *
   * A target that cannot take the drop SAYS SO rather than silently doing
   * nothing: the row draws a dashed outline and this puts the reason in the
   * drag's own region, where it is announced. Leaving a target puts the
   * opening sentence back, so the region always describes what is under the
   * pointer right now.
   */
  function aimAt(id: string | null, over: boolean) {
    const doc = draggingDoc
    if (!doc) return
    if (!over) {
      dragNotice = `Moving “${doc.name}”. Drop on a folder.`
      return
    }
    const verdict = verdictFor(id)
    dragNotice = verdict.ok
      ? `Moving “${doc.name}”. Drop on ${targetFor(id).name}.`
      : `Moving “${doc.name}”. ${verdict.message}`
  }

  /**
   * One object, threaded into the rail as ONE prop (§6b's gate).
   *
   * `TREE_ACCEPTS_DROPS` is the one word that turns decision A off: `false`
   * passes `undefined`, the tree's handlers go inert, and nothing else on this
   * screen changes.
   */
  const dropZone = $derived<DropZone>({
    active: dragDocId !== null,
    accepts: (id) => verdictFor(id).ok,
    aim: aimAt,
    drop: (id, transfer) => performDrop(id, docIdsFrom(transfer)),
  })

  /** A drop target in THIS pane: a folder row, or a breadcrumb ancestor. */
  function paneEnter(event: DragEvent, id: string | null) {
    if (!acceptDrag(event, verdictFor(id).ok)) return
    overId = id
    aimAt(id, true)
  }

  function paneOver(event: DragEvent, id: string | null) {
    // Every frame: a `dragover` that does not preventDefault is a target the
    // browser stops offering, however welcoming `dragenter` was.
    acceptDrag(event, verdictFor(id).ok)
  }

  function paneLeave(id: string | null) {
    if (overId === id) overId = undefined
    aimAt(id, false)
  }

  function paneDrop(event: DragEvent, id: string | null) {
    if (!acceptDrag(event, verdictFor(id).ok)) return
    overId = undefined
    performDrop(id, docIdsFrom(event.dataTransfer))
  }

  /** Is this pane row the one being dragged over, and does it welcome it? */
  function aimState(id: string | null): 'welcome' | 'refuses' | '' {
    if (dragDocId === null || overId !== id) return ''
    return verdictFor(id).ok ? 'welcome' : 'refuses'
  }

  /**
   * The drop itself: apply, PUT, and on a refusal revert out loud.
   *
   * Deliberately NOT through `commit()`. `commit` is for the dialogs: it sets
   * `busy`, which greys the toolbar, and it closes the dialog. A drop has no
   * dialog to close and must not grey the screen for the length of a request
   * whose result is already on it.
   */
  function performDrop(id: string | null, ids: string[]) {
    const doc = ids.length ? (docs.find((d) => d.id === ids[0]) ?? null) : null
    endDocDrag()
    if (!doc) return

    const from = doc.folderId ?? null
    const target = targetFor(id)
    const verdict = canDrop(target, from, doc.name)
    if (!verdict.ok) {
      // Both cases say something. "Already there" is a no-op and belongs in
      // `notice`; a full folder is a refusal and belongs in the alert, where
      // it persists until the next action like every other `actionError`.
      if (verdict.why === 'full') {
        notice = ''
        actionError = verdict.message
      } else {
        actionError = ''
        notice = verdict.message
      }
      return
    }

    // Where it will come back to if the server says no. Read BEFORE the
    // optimistic change, because afterwards the row has already left.
    const backIn = from ? (folders.find((f) => f.id === from)?.name ?? 'where it was') : ROOT_NAME

    void runDrop(async () => {
      try {
        await folderStore.moveDoc(doc, id)
        actionError = ''
        notice = `“${doc.name}” moved to ${target.name}.`
      } catch (err) {
        const cause = err instanceof Reverted ? err.cause : err
        // A 401, or a 403 about the credential, is a different event and still
        // means sign in again. Everything else about a drop is a sentence.
        if (isAuthError(cause)) {
          const message = report(cause, 'That did not work')
          if (message) actionError = message
          return
        }
        // `isMissingError` matches the `access` + ['ref','folderId'] refusal a
        // stale tree produces. It must NOT bounce the person to sign-in, and
        // the honest recovery is to refetch the tree they were dragging into.
        if (isMissingError(cause)) folderStore.invalidate()
        notice = ''
        actionError = revertSentence(doc.name, backIn, refusalMessage(cause, target.name))
        // Focus lands on the row that came back, so the person is standing on
        // the thing that did not move when the alert is read. Never `<body>`.
        await tick()
        focusDocRow(doc.id)
      }
    })
  }

  function focusDocRow(docId: string) {
    const sel = CSS.escape(docId)
    const el =
      document.querySelector<HTMLElement>(`[data-doc-row="${sel}"] .fb-link`) ??
      document.querySelector<HTMLElement>(`[data-tile-id="${sel}"]`)
    el?.focus()
  }

  /* ------------------------------------------------------------- actions */

  /**
   * Run a mutation that has ALREADY been applied to the cache, and put its
   * outcome on screen.
   *
   * This is the old `act()` with the two reloads taken out. The store applies
   * the change, calls the server and reverts to its own snapshot on a refusal,
   * so the only thing left here is which of the three message slots the
   * sentence belongs in.
   */
  async function commit(fn: () => Promise<string>) {
    busy = true
    actionError = ''
    try {
      const said = await fn()
      dialog = null
      dialogError = ''
      notice = said
    } catch (err) {
      // The store rethrows the server's refusal wrapped, so that "we reverted"
      // and "here is why" are not two different questions at this call site.
      const cause = err instanceof Reverted ? err.cause : err
      const message = report(cause, 'That did not work')
      if (!message) return
      // A refusal about the thing in the dialog belongs in the dialog, where
      // the person can fix it and try again without losing what they typed.
      if (dialog && cause instanceof ApiError && (cause.status === 400 || cause.status === 409)) {
        dialogError = cause.detail || message
      } else {
        dialog = null
        actionError = message
      }
    } finally {
      busy = false
    }
  }

  function confirmFolderCreate() {
    const open = dialog
    if (open?.kind !== 'folder-create') return
    const name = open.name.trim()
    const parentId = open.parentId
    void commit(async () => {
      await folderStore.createFolder(name, parentId)
      const where = parentId ? (folders.find((f) => f.id === parentId)?.name ?? 'that folder') : null
      return where ? `Folder "${name}" created in ${where}.` : `Folder "${name}" created in ${ROOT_NAME}.`
    })
  }

  function confirmFolderRename() {
    const open = dialog
    if (open?.kind !== 'folder-rename') return
    const name = open.name.trim()
    void commit(async () => {
      await folderStore.renameFolder(open.folder.id, name)
      return `Folder renamed to "${name}".`
    })
  }

  function confirmDeleteFolder() {
    const open = dialog
    if (open?.kind !== 'folder-delete') return
    const folder = open.folder
    const looking = view.kind === 'folder' && view.id === folder.id
    void commit(async () => {
      const res = await folderStore.deleteFolder(folder.id)
      // Its documents went to the ROOT, so that is where the person should be
      // standing -- not in All documents, which is not a place.
      if (looking) router.go(viewHref({ kind: 'root' }))
      const n = res.unfiled ?? 0
      const m = res.reparented ?? 0
      // Both numbers, because a delete now moves two kinds of thing and a
      // person who sees only one of them has to go looking for the other.
      const parts = [
        n ? `${n} document${n === 1 ? '' : 's'} moved to ${ROOT_NAME}` : '',
        m ? `${m} folder${m === 1 ? '' : 's'} moved up` : '',
      ].filter(Boolean)
      return parts.length
        ? `Folder "${folder.name}" deleted. ${parts.join(', ')}.`
        : `Folder "${folder.name}" deleted.`
    })
  }

  function confirmRename() {
    const open = dialog
    if (open?.kind !== 'rename') return
    const name = open.name.trim()
    void commit(async () => {
      await folderStore.renameDoc(open.doc, name)
      return `Renamed to "${name}".`
    })
  }

  function confirmMove() {
    const open = dialog
    if (open?.kind !== 'move') return
    const target = open.folderId
    void commit(async () => {
      await folderStore.moveDoc(open.doc, target)
      const where = target ? (folders.find((f) => f.id === target)?.name ?? 'a folder') : ROOT_NAME
      return `"${open.doc.name}" moved to ${where}.`
    })
  }

  function confirmDelete() {
    const open = dialog
    if (open?.kind !== 'delete') return
    void commit(async () => {
      await folderStore.deleteDoc(open.doc)
      return `"${open.doc.name}" deleted. You can restore it from Deleted.`
    })
  }

  /**
   * Give up my own access. The answer decides what the sentence says.
   *
   * `stillVisible` is review finding F5 arriving on screen: a person holding
   * both a personal grant and the organisation-wide one still sees the
   * document afterwards, and telling them it was removed while it visibly
   * stays would be the one answer that is not available.
   */
  function confirmLeave() {
    const open = dialog
    if (open?.kind !== 'leave') return
    void commit(async () => {
      const out = await folderStore.leaveDoc(open.doc)
      if (out.stillVisible === 'org') {
        return `You removed your access to "${open.doc.name}". It is still in your list, because it is visible to everyone in your organisation.`
      }
      if (out.stillVisible) {
        return `You removed your access to "${open.doc.name}". It is still in your list, through a folder somebody shared with you.`
      }
      return `"${open.doc.name}" removed from your list.`
    })
  }

  function restore(doc: DocRow) {
    void commit(async () => {
      await folderStore.restoreDoc(doc)
      return `"${doc.name}" restored.`
    })
  }

  /** "New folder in Project A" / "New folder in Root" -- it says WHERE. */
  function createTitle(parentId: string | null): string {
    if (!parentId) return `New folder in ${ROOT_NAME}`
    return `New folder in ${folders.find((f) => f.id === parentId)?.name ?? 'this folder'}`
  }

  function openDialog(next: Dialog) {
    dialogError = ''
    dialog = next
  }

  /** Where this folder's children will land if it is deleted. */
  function parentNameOf(folder: Folder): string {
    return folders.find((f) => f.id === folder.parentId)?.name ?? ROOT_NAME
  }

  /*
   * What deleting this folder will do, in one paragraph and one order: what is
   * in it, that none of it is deleted, where each kind of thing goes, and what
   * is actually lost.
   *
   * It used to be two sentences in two sizes that appeared to disagree -- "the
   * documents move to /" and "anything filed inside it moves up one level into
   * <parent>". Both were true and they are two different rules: a document in
   * the folder becomes unfiled (it lands at the root), while a SUBFOLDER moves
   * up into this folder's parent. Said separately, in different type, they read
   * as a contradiction.
   */
  function deleteFolderNote(folder: Folder): string {
    const docs = folder.docCount
    const subs = folders.filter((f) => f.parentId === folder.id).length
    const parent = parentNameOf(folder)
    const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

    if (docs === 0 && subs === 0) {
      return `“${folder.name}” is empty, so deleting it removes the folder and nothing else.`
    }

    const holds =
      docs > 0 && subs > 0
        ? `${n(docs, 'document', 'documents')} and ${n(subs, 'folder', 'folders')}`
        : docs > 0
          ? n(docs, 'document', 'documents')
          : n(subs, 'folder', 'folders')

    // Where each kind goes. When the parent IS the root the two destinations
    // are the same place, so they are said once.
    const docsGo = `${docs === 1 ? 'it moves' : 'they move'} to ${ROOT_NAME}`
    const subsGo = `${subs === 1 ? 'it moves' : 'they move'} up into ${parent}`
    const goes =
      docs > 0 && subs > 0
        ? parent === ROOT_NAME
          ? `all of it moves to ${ROOT_NAME}`
          : `the document${docs === 1 ? '' : 's'} move${docs === 1 ? 's' : ''} to ${ROOT_NAME} and the folder${subs === 1 ? '' : 's'} move${subs === 1 ? 's' : ''} up into ${parent}`
        : docs > 0
          ? docsGo
          : subsGo

    return `“${folder.name}” holds ${holds}. Deleting the folder does not delete ${docs + subs === 1 ? 'it' : 'any of them'} — ${goes}. Only the folder itself is gone, and that cannot be undone.`
  }

  function when(iso: string): string {
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
  }
</script>

<svelte:head><title>Unomena Docs</title></svelte:head>
<svelte:window bind:innerWidth={viewportWidth} />

<TopBar>
  <a class="linkish" href="/settings/mcp" onclick={(e) => navigate(e, '/settings/mcp')}>MCP</a>
</TopBar>

<main>
  <div class="measure measure-wide browser">
    <AuthErrorNotice />

    <!--
      The page still has a name, for anyone navigating by headings -- it is
      only no longer DRAWN. The visible "Documents" and its tagline took a band
      the height of the file browser's first three rows to say what the address
      bar and the tree already say.
    -->
    <h1 class="visually-hidden">Documents</h1>

    {#if stacked}
      <!-- Below 640px the rail is behind a disclosure and the list is full
           width: a 180px tree beside a list on a phone leaves neither of them
           readable. -->
      <button
        type="button"
        class="rail-disclosure"
        aria-expanded={railOpen}
        onclick={() => (railOpen = !railOpen)}
      >
        <span class="disclosure" class:open={railOpen}><Icon name="chevron" size="0.85rem" /></span> Folders
      </button>
    {/if}

    <!--
      The two breakpoints of §4 are expressed HERE, in one place, from
      `viewportWidth`, rather than half in JavaScript and half in a media
      query. The `46rem` rule this file used to carry said one column at 736px,
      which left an undefined band between it and §4's 640px where the rail was
      a disclosure by one rule and a stacked column by the other.

      A drag writes `--rail-width` and nothing else: one custom property
      changes, and neither pane re-renders.
    -->
    <div
      class="layout"
      class:stacked
      class:no-divider={!resizable}
      style={`--rail-width:${railTrack}px`}
    >
      {#if !stacked || railOpen}
        <aside class="rail-column">
          <!-- Decision A (§6b). `TREE_ACCEPTS_DROPS` is the one word that
               turns the tree's drop target off: `false` passes `undefined`,
               every handler in the tree goes inert, and nothing else on this
               screen changes. -->
          <FolderRail
            {folders}
            {view}
            onselect={select}
            onhover={warm}
            zone={TREE_ACCEPTS_DROPS ? dropZone : undefined}
          />
          {#if folderStore.error && folders.length === 0}
            <!-- A rail that will not load must not take the list down with it:
                 the documents are the page, the arrangement is the furniture.
                 It is also not an error screen -- the next focus revalidates. -->
            <p class="rail-trouble">Could not load your folders.</p>
          {/if}
        </aside>
      {/if}

      {#if resizable}
        <RailDivider
          width={railPx}
          min={RAIL_MIN}
          max={railMax}
          defaultWidth={RAIL_DEFAULT}
          onwidth={(px) => (railWidth = px)}
          oncommit={persistRail}
        />
      {/if}

      <section class="list-column" aria-busy={pendingDocs}>
        <div class="pane-head">
          <div class="pane-where" class:blank={blankWhere}>
            <nav class="crumbs" aria-label="Folder path">
              {#each crumbs as crumb, i (crumb.id ?? crumb.name)}
                {#if i < crumbs.length - 1}
                  <!-- A crumb is a drop target too (§6a): dragging to the
                       parent is the other common gesture and it is the
                       cheapest way to move a document UP. The LAST crumb is
                       not one, because it is the place you are already in --
                       "moved it to where it already is" is the no-op §6a
                       names, and it is not highlighted and says nothing. -->
                  <a
                    class="crumb"
                    class:drop-welcome={aimState(crumb.id) === 'welcome'}
                    class:drop-refuses={aimState(crumb.id) === 'refuses'}
                    href={viewHref(viewOf(crumb))}
                    aria-label={crumb.root ? ROOT_NAME : undefined}
                    onclick={(e) => follow(e, viewOf(crumb))}
                    onpointerenter={() => warm(viewOf(crumb))}
                    ondragenter={(e) => paneEnter(e, crumb.id)}
                    ondragover={(e) => paneOver(e, crumb.id)}
                    ondragleave={(e) => {
                      if (leftTarget(e)) paneLeave(crumb.id)
                    }}
                    ondrop={(e) => paneDrop(e, crumb.id)}
                  >
                    {crumb.root ? ROOT_GLYPH : crumb.name}
                  </a>
                  <!--
                    No separator after the ROOT crumb. The root is drawn as `/`,
                    and a `/` separator behind it renders `/ / Samples` -- the
                    glyph IS the separator in every file path ever written, so
                    the one that follows it is the duplicate, not the crumb.
                  -->
                  {#if !crumb.root}
                    <span class="crumb-sep" aria-hidden="true">/</span>
                  {/if}
                {:else}
                  <span class="crumb crumb-here" aria-label={crumb.root ? ROOT_NAME : undefined}>
                    {crumb.root ? ROOT_GLYPH : crumb.name}
                  </span>
                {/if}
              {/each}
            </nav>
            <!-- The root is DRAWN as `/` and ANNOUNCED as "Root": the user asked
                 for the glyph, and a heading that announces as a punctuation
                 mark tells a screen reader nothing. One `aria-label`, one
                 string each, both from `doclist-types.ts`. -->
            <h2 class="list-title" aria-label={here?.root ? ROOT_NAME : undefined}>
              {here?.root ? ROOT_GLYPH : (here?.name ?? ROOT_NAME)}
            </h2>
          </div>

          <!-- At the right end of the breadcrumb line, ABOVE the toolbar: it
               changes how the pane is drawn, not what is in it. -->
          <ViewSwitch value={pane} onchange={setPane} />

          <FolderToolbar
            {view}
            {busy}
            onrename={() => {
              if (current) openDialog({ kind: 'folder-rename', folder: current, name: current.name })
            }}
            oncreate={() =>
              openDialog({
                kind: 'folder-create',
                parentId: view.kind === 'folder' ? view.id : null,
                name: '',
              })}
            ondelete={() => {
              if (current) openDialog({ kind: 'folder-delete', folder: current })
            }}
          />
        </div>

        <!-- The whole pending affordance. It occupies its 2px whether or not
             anything is in flight, so a load cannot move the list under the
             pointer; only its colour changes. No spinner, and nothing over the
             content. -->
        <div class="pane-progress" class:busy={pendingDocs} aria-hidden="true"></div>

        {#if notice}<p class="flash" role="status">{notice}</p>{/if}
        {#if actionError}<p class="flash flash-bad" role="alert">{actionError}</p>{/if}
        <!-- The drag's OWN region (§6c). Three messages, three independent
             nodes: `notice` is cleared by every view change, and a drag
             announcement parked in it would be wiped by the navigation the
             drag caused. It lives only for the length of the gesture.

             It is FIXED, not in the list's flow (BUG-022): a line appearing
             above the table moves every row, which cancels the drag outright
             in Chromium and, later in the gesture, shuffles the drop targets
             under the pointer. Announcement is unchanged -- it is the same
             role="status" node -- and it cannot be pointed at, so it can never
             become the thing under the cursor at the moment of the drop. -->
        {#if dragNotice}<p class="flash flash-drag drag-toast" role="status">{dragNotice}</p>{/if}

        <!--
          ICON VIEW ONLY, and that is the whole of this branch's purpose.

          The tile grid is out of scope for the density work, so its folder
          rows keep the two-line shape they shipped with. List view draws its
          folders as rows of the SAME table as its documents, below, which is
          the only way the two can share a column grid.
        -->
        {#if icons && isPlace && paneFolders.length > 0}
          <ul class="doclist folderlist">
            {#each paneFolders as folder (folder.id)}
              <li class="doclist-row">
                {#if folder.pending}
                  <!-- Created a moment ago and not yet confirmed. It is drawn,
                       because the point is that the screen does not wait -- but
                       its id is a local placeholder, so it is not a link:
                       following it would ask for a folder that does not exist. -->
                  <span class="doclist-link doclist-provisional">
                    <span class="doclist-name">
                      <Icon name="folder" />{folder.name}
                    </span>
                    <span class="doclist-meta"><span>creating…</span></span>
                  </span>
                {:else}
                  <a
                    class="doclist-link"
                    href={viewHref({ kind: 'folder', id: folder.id })}
                    onclick={(e) => follow(e, { kind: 'folder', id: folder.id })}
                    onpointerenter={() => warm({ kind: 'folder', id: folder.id })}
                    onfocus={() => warm({ kind: 'folder', id: folder.id })}
                  >
                    <span class="doclist-name">
                      <Icon name="folder" />{folder.name}
                    </span>
                    <span class="doclist-meta">
                      <span>{folder.docCount} document{folder.docCount === 1 ? '' : 's'}</span>
                    </span>
                  </a>
                {/if}
              </li>
            {/each}
          </ul>
        {/if}

        <!--
          Everything the pane says when it has no documents to draw, in ONE
          place. Both presentations render it: an empty folder says the same
          thing whichever way its documents would have been drawn, and two
          copies of five messages is how one of them ends up saying something
          the other does not.
        -->
        {#snippet tail()}
          {#if loadError}
            <p class="empty">Could not load documents: {loadError}.</p>
            <div class="options">
              <button
                type="button"
                class="btn btn-quiet btn-small"
                onclick={() => folderStore.ensureDocs(view, { force: true }).catch((e) => loadFailed(view, e))}
              >
                Try again
              </button>
            </div>
          {:else if archived}
            <p class="empty">Nothing deleted.</p>
            <p class="empty">
              A document you delete for everyone waits here until it is purged, and can be
              restored until then.
            </p>
          {:else if isPlace && paneFolders.length > 0}
            <!-- Not an empty state: the pane HAS contents, they are just all
                 folders. A quiet line, because the question "where are the
                 documents" still deserves an answer. -->
            <p class="quiet">No documents in this folder.</p>
          {:else if view.kind === 'folder'}
            <!-- Tiles have no row to put it in, so the same sentence stands on
                 its own. The list view says it inside the table (`emptyPlace`),
                 which is why this branch is not reached there. -->
            <p class="empty centred">Nothing here to see.</p>
          {:else}
            <!-- An empty ROOT means no folders and no documents at all, which
                 is the first-run screen: the only thing worth saying on it is
                 how to get a document. -->
            <p class="empty">No documents yet.</p>
            <p class="empty">
              Connect an MCP client and ask it to write one — you can
              <a href="/settings/mcp" onclick={(e) => navigate(e, '/settings/mcp')}
                >mint a token</a
              > for it here.
            </p>
          {/if}
        {/snippet}

        {#if stuck}
          <p class="empty">{stuck}</p>
          <p class="empty"><a href="/login">Go to sign in</a></p>
        {:else if icons}
          {#if docs.length > 0}
            <!--
              `role="grid"`, with a `role="row"` per VISUAL row (§9). A wrapped
              tile layout has no reading order a linear walk can recover, so it
              gets two-dimensional arrow keys and a roving tabindex, and the
              tile's menu button lives inside the cell.

              The sketches are NOT awaited here: every tile is on screen with
              its name and its metadata on the first frame, and the shape
              arrives afterwards or not at all.
            -->
            <div
              class="tilegrid"
              class:after-folders={isPlace && paneFolders.length > 0}
              role="grid"
              aria-label="Documents"
              aria-rowcount={tileRows.length}
              aria-colcount={columns}
              bind:clientWidth={gridWidth}
              style={`--tile-min:${TILE_MIN}px; --tile-gap:${TILE_GAP}px`}
            >
              {#each tileRows as row, r (r)}
                <div class="tilerow" role="row">
                  {#each row as doc, c (doc.id)}
                    <DocTile
                      {doc}
                      {archived}
                      tabindex={r * columns + c === activeTile ? 0 : -1}
                      onkey={tileKey}
                      onshare={(d) => openDialog({ kind: 'share', doc: d })}
                      onrename={(d) => openDialog({ kind: 'rename', doc: d, name: d.name })}
                      onmove={(d) => openDialog({ kind: 'move', doc: d, folderId: d.folderId })}
                      ondelete={(d) => openDialog({ kind: 'delete', doc: d })}
                      onleave={(d) => openDialog({ kind: 'leave', doc: d })}
                      onrestore={restore}
                    />
                  {/each}
                </div>
              {/each}
            </div>
          {:else if unseen && pendingDocs}
            <!-- The ONE case with nothing cached to repaint: a place this tab
                 has never had an answer for. Three inert rows at the height of
                 a real one, so the pane has the shape it is about to have
                 rather than a spinner in a hole. -->
            <ul class="doclist" class:after-folders={isPlace && paneFolders.length > 0} aria-hidden="true">
              <li class="doclist-row"><span class="doclist-link placeholder-row"><span class="skeleton-line skeleton-a"></span></span></li>
              <li class="doclist-row"><span class="doclist-link placeholder-row"><span class="skeleton-line skeleton-b"></span></span></li>
              <li class="doclist-row"><span class="doclist-link placeholder-row"><span class="skeleton-line skeleton-c"></span></span></li>
            </ul>
          {:else}
            {@render tail()}
          {/if}
        {:else}
          <!--
            LIST VIEW: ONE table for the folders and the documents.

            One table and not two lists, because the promise of this screen is
            that the columns line up across both kinds of row -- and two
            grids, however carefully their widths are written, line up only
            until somebody's name is one character longer. A table's own
            layout algorithm is the mechanism that keeps a folder's row and a
            document's row on the same column boundaries, and it is the reason
            the boundary between the two groups is now exactly one rule: every
            row draws its own `border-bottom` and there is no second list with
            a `border-top` of its own to double it.

            The header cells are real `<th>`s carrying `aria-sort`, and each
            holds a real `<button>` -- the entire accessible story of a
            sortable table, and both halves of it are trivially easy to leave
            out.

            No virtualisation, and none is needed: TASK-039 caps a folder at
            100 direct children, so 100 rows is the ABSOLUTE worst case and a
            hundred 37px rows is ~3,700px. An `IntersectionObserver` here
            would be machinery bought for a case the server does not permit.
          -->
          {#if hasRows || (unseen && pendingDocs) || emptyPlace}
            <table class="fb" aria-label="Folders and documents">
              <thead>
                <tr>
                  <th scope="col" class="c-name" aria-sort={ariaSortOf(sort, 'name')}>
                    <button type="button" class="fb-sort" onclick={() => applySort('name')}>
                      Name<span class="fb-arrow" class:on={sort.key === 'name'} aria-hidden="true"
                        >{arrow('name')}</span
                      >
                    </button>
                  </th>
                  {#if showWhen}
                    <th scope="col" class="c-when" aria-sort={ariaSortOf(sort, 'updated')}>
                      <button type="button" class="fb-sort" onclick={() => applySort('updated')}>
                        <!-- In Trash the column shows when it was deleted, so
                             that is what it is called and what it sorts by. -->
                        {archived ? 'Deleted' : 'Updated'}<span
                          class="fb-arrow"
                          class:on={sort.key === 'updated'}
                          aria-hidden="true">{arrow('updated')}</span
                        >
                      </button>
                    </th>
                  {/if}
                  {#if showRev}
                    <th scope="col" class="c-rev" aria-sort={ariaSortOf(sort, 'rev')}>
                      <button type="button" class="fb-sort" onclick={() => applySort('rev')}>
                        Rev<span class="fb-arrow" class:on={sort.key === 'rev'} aria-hidden="true"
                          >{arrow('rev')}</span
                        >
                      </button>
                    </th>
                  {/if}
                  {#if showBy}
                    <th scope="col" class="c-by" aria-sort={ariaSortOf(sort, 'by')}>
                      <button type="button" class="fb-sort" onclick={() => applySort('by')}>
                        By<span class="fb-arrow" class:on={sort.key === 'by'} aria-hidden="true"
                          >{arrow('by')}</span
                        >
                      </button>
                    </th>
                  {/if}
                  <!-- The actions column is not sortable and has no visible
                       heading; it still needs a name, for the same reason
                       every other column has one. -->
                  <th scope="col" class="c-actions"><span class="fb-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <!--
                  FOLDERS FIRST, ALWAYS, as a group -- see `doclist-sort.ts`.
                  This is the file-browser convention and it is what keeps
                  "sort by name" useful when a folder and a document share a
                  prefix. It is not an oversight: do not merge the two `each`
                  blocks into one sorted list.
                -->
                {#each order.folders as folder (folder.id)}
                  <!-- A folder row is a drop target (§6a). The affordance is
                       an outline and a tint and exists only while something is
                       dragged over it, so the table looks identical at rest; a
                       folder that cannot take the drop draws a DASHED outline
                       and says why in `dragNotice`, rather than swallowing the
                       gesture. A provisional folder is not a target: its id is
                       a local placeholder the server has never seen. -->
                  <tr
                    class="fb-row"
                    class:drop-welcome={!folder.pending && aimState(folder.id) === 'welcome'}
                    class:drop-refuses={!folder.pending && aimState(folder.id) === 'refuses'}
                    ondragenter={(e) => {
                      if (!folder.pending) paneEnter(e, folder.id)
                    }}
                    ondragover={(e) => {
                      if (!folder.pending) paneOver(e, folder.id)
                    }}
                    ondragleave={(e) => {
                      if (!folder.pending && leftTarget(e)) paneLeave(folder.id)
                    }}
                    ondrop={(e) => {
                      if (!folder.pending) paneDrop(e, folder.id)
                    }}
                  >
                    <td class="c-name">
                      <div class="fb-cell">
                        {#if folder.pending}
                          <!-- Created a moment ago and not yet confirmed. It is
                               drawn, because the point is that the screen does
                               not wait -- but its id is a local placeholder, so
                               it is not a link: following it would ask for a
                               folder that does not exist. -->
                          <span class="fb-link fb-provisional">
                            <Icon name="folder" />
                            <span class="fb-text">{folder.name}</span>
                          </span>
                        {:else}
                          <a
                            class="fb-link"
                            href={viewHref({ kind: 'folder', id: folder.id })}
                            onclick={(e) => follow(e, { kind: 'folder', id: folder.id })}
                            onpointerenter={() => warm({ kind: 'folder', id: folder.id })}
                            onfocus={() => warm({ kind: 'folder', id: folder.id })}
                          >
                            <Icon name="folder" />
                            <span class="fb-text">{folder.name}</span>
                          </a>
                        {/if}
                      </div>
                    </td>
                    {#if showWhen}
                      <!-- A folder has no date of its own, so the widest
                           column carries what a file browser puts there
                           instead: what is in it. -->
                      <td class="c-when"
                        >{#if folder.pending}creating…{:else}{folder.docCount} document{folder.docCount ===
                          1
                            ? ''
                            : 's'}{/if}</td
                      >
                    {/if}
                    {#if showRev}<td class="c-rev"></td>{/if}
                    {#if showBy}<td class="c-by"></td>{/if}
                    <td class="c-actions"></td>
                  </tr>
                {/each}

                {#each order.docs as doc (doc.id)}
                  <!--
                    The DRAG SOURCE (§6a). `draggable` on the row rather than
                    on the name, so the whole row is the handle; the name is
                    still an `<a href>`, so a drag that starts on it carries
                    our payload as well as the browser's own link data, and a
                    plain click still navigates.

                    An archived row is not draggable: Trash is not a place, and
                    restoring is a different gesture with a different meaning
                    (§8). Every one of these rows also carries Move to… in its
                    action set, which is the keyboard and touch path and is not
                    a fallback to be retired (§6c).
                  -->
                  <tr
                    class="fb-row"
                    data-doc-row={doc.id}
                    class:fb-dragging={dragDocId === doc.id}
                    draggable={!archived}
                    ondragstart={(e) => {
                      if (!archived) startDocDrag(e, doc)
                    }}
                    ondragend={endDocDrag}
                  >
                    <td class="c-name">
                      <div class="fb-cell">
                        {#if archived}
                          <!-- Not a link: an archived document cannot be opened,
                               by anybody, until it is restored. Offering a link
                               that leads to a refusal would be a worse answer
                               than not offering one. -->
                          <span class="fb-link fb-dead">
                            <Icon name="documents" />
                            <span class="fb-text">{doc.name}</span>
                          </span>
                        {:else}
                          <a
                            class="fb-link"
                            href={`/d/${doc.id}`}
                            onclick={(e) => navigate(e, `/d/${doc.id}`)}
                          >
                            <Icon name="documents" />
                            <span class="fb-text">{doc.name}</span>
                          </a>
                          {#if doc.role !== 'owner'}<span class="fb-role">{doc.role}</span>{/if}
                        {/if}
                      </div>
                    </td>
                    {#if showWhen}
                      <!-- Short and absolute, with the full value in the
                           `title`. The reasoning is on `SHORT_WHEN`. -->
                      <td class="c-when">
                        {#if archived}
                          {#if doc.archivedAt}<span title={when(doc.archivedAt)}
                              >{shortWhen(doc.archivedAt)}</span
                            >{/if}
                        {:else}
                          <span title={when(doc.updated_at)}>{shortWhen(doc.updated_at)}</span>
                        {/if}
                      </td>
                    {/if}
                    {#if showRev}<td class="c-rev">{doc.rev}</td>{/if}
                    {#if showBy}<td class="c-by" title={doc.created_by ?? undefined}
                        >{doc.created_by ?? ''}</td
                      >{/if}
                    <td class="c-actions">
                      <!-- Still three buttons, still one click each. `dense`
                           collapses the labels to the glyphs the narrow
                           viewport already uses; it is emphatically not a
                           menu. -->
                      <DocRowActions
                        {doc}
                        {archived}
                        dense
                        onshare={(d) => openDialog({ kind: 'share', doc: d })}
                        onrename={(d) => openDialog({ kind: 'rename', doc: d, name: d.name })}
                        onmove={(d) => openDialog({ kind: 'move', doc: d, folderId: d.folderId })}
                        ondelete={(d) => openDialog({ kind: 'delete', doc: d })}
                        onleave={(d) => openDialog({ kind: 'leave', doc: d })}
                        onrestore={restore}
                      />
                    </td>
                  </tr>
                {/each}

                {#if unseen && pendingDocs}
                  <!-- The ONE case with nothing cached to repaint. Three inert
                       rows at the height of a real one and in the same
                       columns, so when the answer lands the rows are replaced
                       and NOTHING reflows. -->
                  {#each ['skeleton-a', 'skeleton-b', 'skeleton-c'] as width (width)}
                    <tr class="fb-row" aria-hidden="true">
                      <td class="c-name"><span class="skeleton-line fb-skel {width}"></span></td>
                      {#if showWhen}<td class="c-when"></td>{/if}
                      {#if showRev}<td class="c-rev"></td>{/if}
                      {#if showBy}<td class="c-by"></td>{/if}
                      <td class="c-actions"></td>
                    </tr>
                  {/each}
                {/if}
                {#if emptyPlace && !(unseen && pendingDocs)}
                  <tr class="fb-empty">
                    <td colspan={fbColumns}>Nothing here to see.</td>
                  </tr>
                {/if}
              </tbody>
            </table>
          {/if}

          {#if docs.length === 0 && !(unseen && pendingDocs) && !emptyPlace}
            {@render tail()}
          {/if}
        {/if}
      </section>
    </div>
  </div>
</main>

{#if dialog?.kind === 'share'}
  <!--
    Its own component rather than a `ModalSheet`, because it is not a confirm:
    it stays open while it works, issues one request per address with visible
    progress, and carries the access list underneath. `onchanged` refreshes
    nothing about this screen today -- the row's own role cannot change by
    sharing it with somebody else -- and exists so that the one thing which
    does change later (phase 3's transfer) has a seam to hang on.
  -->
  <ShareDialog
    doc={dialog.doc}
    onclose={() => (dialog = null)}
    onchanged={() => folderStore.invalidate()}
  />
{:else if dialog?.kind === 'leave'}
  <ModalSheet
    title={`Remove "${dialog.doc.name}" from your list?`}
    confirmLabel="Remove it"
    danger
    {busy}
    error={dialogError}
    onconfirm={confirmLeave}
    oncancel={() => (dialog = null)}
  >
    <p>
      You give up your own access. The document is not deleted, and everyone else keeps theirs.
    </p>
    <p class="field-note">
      Only its owner can share it with you again — you cannot let yourself back in.
    </p>
  </ModalSheet>
{:else if dialog?.kind === 'rename'}
  <ModalSheet
    title="Rename document"
    confirmLabel="Rename"
    {busy}
    error={dialogError}
    confirmDisabled={!dialog.name.trim()}
    onconfirm={confirmRename}
    oncancel={() => (dialog = null)}
  >
    <label class="field">
      <span class="field-label">Name</span>
      <!-- svelte-ignore a11y_autofocus -->
      <input class="field-input" bind:value={dialog.name} maxlength="200" autofocus />
    </label>
    <p class="field-note">
      There is one name and everyone sees it. Anyone reading the document sees it change.
    </p>
  </ModalSheet>
{:else if dialog?.kind === 'folder-rename'}
  <!-- A dialog, not the rail's old inline edit. Inline was reasonable in a
       12rem column; on a wide pane beside a breadcrumb it is a text input
       pretending to be a heading. -->
  <ModalSheet
    title="Rename folder"
    confirmLabel="Rename"
    {busy}
    error={dialogError}
    confirmDisabled={!dialog.name.trim()}
    onconfirm={confirmFolderRename}
    oncancel={() => (dialog = null)}
  >
    <label class="field">
      <span class="field-label">Name</span>
      <!-- svelte-ignore a11y_autofocus -->
      <input class="field-input" bind:value={dialog.name} maxlength="60" autofocus />
    </label>
    <p class="field-note">Where you file a document is yours alone; nobody else sees it.</p>
  </ModalSheet>
{:else if dialog?.kind === 'folder-create'}
  <ModalSheet
    title={createTitle(dialog.parentId)}
    confirmLabel="Create"
    {busy}
    error={dialogError}
    confirmDisabled={!dialog.name.trim()}
    onconfirm={confirmFolderCreate}
    oncancel={() => (dialog = null)}
  >
    <label class="field">
      <span class="field-label">Name</span>
      <!-- svelte-ignore a11y_autofocus -->
      <input class="field-input" bind:value={dialog.name} maxlength="60" autofocus />
    </label>
    <p class="field-note">It is created inside the folder this pane is showing.</p>
  </ModalSheet>
{:else if dialog?.kind === 'move'}
  <ModalSheet
    title={`Move "${dialog.doc.name}"`}
    confirmLabel="Move"
    {busy}
    error={dialogError}
    onconfirm={confirmMove}
    oncancel={() => (dialog = null)}
  >
    <div class="choices" role="radiogroup" aria-label="Folder">
      <label class="choice">
        <input type="radio" value={null} bind:group={dialog.folderId} />
        <!-- Drawn as `/`, announced as "Root": the same pair as the crumb and
             the tree row, from the same two constants. -->
        <span aria-label={ROOT_NAME}>{ROOT_GLYPH}</span>
      </label>
      <!-- Indented, in tree order: the radio list is the whole move dialog, so
           a nested folder that read as a sibling would be a wrong answer you
           could not see. It is also the keyboard equivalent of the drag §6
           adds, which is why it stays in the primary action set. -->
      {#each movable as entry (entry.folder.id)}
        <label class="choice" style={`padding-left:${0.4 + Math.min(entry.depth, 5) * 0.75}rem`}>
          <input type="radio" value={entry.folder.id} bind:group={dialog.folderId} />
          <span>{entry.folder.name}</span>
        </label>
      {/each}
    </div>
    <p class="field-note">Where you file a document is yours alone; nobody else sees it.</p>
  </ModalSheet>
{:else if dialog?.kind === 'delete'}
  <ModalSheet
    title={`Delete "${dialog.doc.name}"?`}
    confirmLabel="Delete for everyone"
    danger
    {busy}
    error={dialogError}
    onconfirm={confirmDelete}
    oncancel={() => (dialog = null)}
  >
    <p>
      It leaves every list, including anyone else's, and nobody can open it — you included.
    </p>
    <p class="field-note">
      It moves to <strong>Trash</strong> in the rail, where you can restore it.
    </p>
  </ModalSheet>
{:else if dialog?.kind === 'folder-delete'}
  <ModalSheet
    title={`Delete the folder "${dialog.folder.name}"?`}
    confirmLabel="Delete folder"
    danger
    {busy}
    error={dialogError}
    onconfirm={confirmDeleteFolder}
    oncancel={() => (dialog = null)}
  >
    <!-- One paragraph, one size: the two-sentence version read as a
         contradiction. See `deleteFolderNote`. -->
    <p>{deleteFolderNote(dialog.folder)}</p>
  </ModalSheet>
{/if}

<style>
  /* The file browser starts close under the top bar now there is no page
     heading above it; the shared .measure padding was sized to sit a title in. */
  .browser {
    padding-block-start: clamp(1.25rem, 3vw, 2rem);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }

  /* Three tracks: the rail, the divider, the list. The first is a custom
     property so a drag updates one variable. The class variants are the two
     breakpoints of §4 and are set from `viewportWidth`, so the divider's
     existence and the grid it sits in cannot disagree. */
  .layout {
    display: grid;
    grid-template-columns: var(--rail-width, 260px) auto minmax(0, 1fr);
    gap: 0 1rem;
    align-items: start;
  }

  .layout.no-divider {
    grid-template-columns: var(--rail-width, 260px) minmax(0, 1fr);
    gap: 0 2rem;
  }

  .layout.stacked {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.25rem;
  }

  .rail-column {
    position: sticky;
    top: 1rem;
    min-width: 0;
  }

  .layout.stacked .rail-column {
    position: static;
  }

  .rail-trouble {
    margin: 0.5rem 0 0;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  .rail-disclosure {
    display: inline-flex;
    align-items: baseline;
    gap: 0.35rem;
    margin-bottom: 0.5rem;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.375rem;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: var(--text-meta, 0.8125rem);
    padding: 0.3rem 0.6rem;
    cursor: pointer;
  }

  .list-column {
    min-width: 0;
  }

  .pane-head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin-bottom: 0.5rem;
  }

  .pane-where {
    min-width: 0;
  }

  /* Kept, not removed: see `blankWhere`. */
  .pane-where.blank {
    visibility: hidden;
  }

  .crumbs {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.25rem;
    margin-bottom: 0.35rem;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  .crumb {
    color: inherit;
    text-decoration: none;
  }

  .crumb:hover {
    color: var(--color-ink, #211d18);
    text-decoration: underline;
  }

  .crumb-here {
    color: var(--color-ink, #211d18);
  }

  .list-title {
    margin: 0;
    font-size: 1.05rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--color-ink-soft, #6e665c);
  }

  .doclist-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .doclist-row :global(.doclist-link) {
    flex: 1 1 auto;
    min-width: 0;
  }

  /*
   * ONE rule between the folder list and the document list, not two.
   *
   * `.doclist` carries a `border-top` and each of its rows a `border-bottom`,
   * so a folder list stacked above a document list drew the last folder row's
   * bottom rule AND the document list's own top rule -- two 1px lines with a
   * 4px margin between them, which reads as a doubled, slightly broken border.
   * The boundary is a single rule now: the folder list keeps its rows' bottom
   * border and the list below it drops its top one.
   *
   * Expressed as a class on the second list rather than as `.folderlist +
   * .doclist`, because the two are not always adjacent elements -- the
   * placeholder list, the quiet line and the empty states all sit in that slot
   * -- and a sibling selector that silently stops matching is a border that
   * comes back without anybody editing the rule.
   */
  .folderlist {
    margin-bottom: 0;
  }

  .doclist.after-folders {
    border-top: none;
  }

  /* One grid, one column count. `--tile-min` and `--tile-gap` are set from the
     same two constants the arrow-key handler divides by, so the number of
     `role="row"` groups and the number of tiles on a visual row cannot
     disagree -- which they would the moment one of them was a magic number in
     a stylesheet. */
  .tilegrid {
    display: flex;
    flex-direction: column;
    gap: var(--tile-gap, 12px);
    margin-top: 0.75rem;
  }

  .tilerow {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--tile-min, 176px), 1fr));
    gap: var(--tile-gap, 12px);
    align-items: stretch;
  }

  .tilegrid.after-folders {
    margin-top: 0.75rem;
  }

  /* The pending affordance, and the whole of it. It holds its 2px at all
     times, so nothing below it moves when a request starts or ends; only the
     colour changes. A spinner over the content, or a cleared pane, is what
     this exists instead of. */
  .pane-progress {
    height: 2px;
    margin-bottom: 0.35rem;
    border-radius: 999px;
    background: transparent;
    transition: background-color 0.18s ease;
  }

  .pane-progress.busy {
    background: linear-gradient(
      90deg,
      transparent,
      var(--color-ink-soft, #6e665c) 50%,
      transparent
    );
    opacity: 0.45;
  }

  @media (prefers-reduced-motion: reduce) {
    .pane-progress {
      transition: none;
    }
  }

  /* A row that is standing in for one that has not arrived. Same height as a
     real row, so the pane has the shape it is about to have. */
  .placeholder-row {
    display: block;
    padding: 1.5rem 0.25rem;
  }

  /* `.skeleton-line` is sized in the shared stylesheet and is block-level
     there; inside a span it needs saying again, or the placeholder rows
     collapse to nothing and the pane has a hole in it after all. */
  .placeholder-row .skeleton-line {
    display: block;
    margin: 0;
  }

  .doclist-provisional {
    display: block;
    color: var(--color-ink-soft, #6e665c);
  }

  /* --------------------------------------------------- the dense list view
   *
   * One table, ~37px a row: 36px of row plus the 1px rule it draws under
   * itself. A hundred rows -- the most a folder can hold, TASK-039 -- is
   * ~3,700px, which is four screens rather than nine, and is why there is no
   * virtualisation here and no need for any.
   *
   * `table-layout: auto` is doing real work: Name is the one flexible column
   * (`width: 100%; max-width: 0` is the idiom that makes a table cell yield
   * its intrinsic width and take the leftover instead), every other column
   * sizes to its own content, and a folder row and a document row are
   * columns of the same table rather than two grids that agree until they
   * do not.
   */
  /*
   * app.css styles `th, td` globally with a border-right -- correct for a
   * markdown table INSIDE a document, where column rules help a reader follow
   * a row. A file list is not that table: the columns are metadata about one
   * object, and ruling them turns a quiet list into a spreadsheet. The rows
   * still separate; the columns do not.
   */
  .fb th,
  .fb td {
    border-right: 0;
  }

  .fb {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
    /* Nothing in a cell may push the table wider than the pane: the name
       column truncates instead. */
    table-layout: auto;
  }

  /*
   * The header stays put, which is what makes a hundred rows navigable.
   *
   * `top` is the top bar's own height, because the top bar is `position:
   * sticky` at `z-index: 50` -- a header that stuck to 0 would slide under it
   * and be unreadable for the length of the list. The z-index here is
   * deliberately far below 50 for the same reason.
   *
   * The rules are `box-shadow: inset`, not borders: a sticky cell in a
   * `border-collapse: collapse` table leaves its borders behind when it
   * detaches, which is a header that loses its underline halfway down the
   * page.
   */
  .fb thead th {
    position: sticky;
    top: 4.25rem;
    z-index: 2;
    height: 2rem;
    padding: 0 0.5rem;
    text-align: left;
    font-weight: 500;
    white-space: nowrap;
    background: var(--color-paper, #faf9f5);
    box-shadow:
      inset 0 1px 0 var(--color-line, #211d1824),
      inset 0 -1px 0 var(--color-line, #211d1824);
  }

  .fb-sort {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    color: var(--color-ink-soft, #6e665c);
    font: inherit;
    font-size: var(--text-meta, 0.8125rem);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 0.2rem 0.3rem;
    margin-inline: -0.3rem;
    cursor: pointer;
  }

  .fb-sort:hover {
    background: var(--color-plinth, #f1ece4);
    color: var(--color-ink, #211d18);
  }

  .fb-arrow {
    font-size: 0.7rem;
    line-height: 1;
    opacity: 0;
  }

  .fb-arrow.on {
    opacity: 1;
    color: var(--color-ink, #211d18);
  }

  .fb-row td {
    height: 2.25rem;
    padding: 0 0.5rem;
    vertical-align: middle;
    border-bottom: 1px solid var(--color-line, #211d1824);
  }

  /* The single rule between the folders and the documents falls out of this:
     every row draws one line under itself and there is no second list with a
     `border-top` of its own to double it. */
  .fb-row:hover td {
    background: var(--color-plinth, #f1ece4);
  }

  .fb th:first-child,
  .fb td:first-child {
    padding-left: 0.25rem;
  }

  .fb th:last-child,
  .fb td:last-child {
    padding-right: 0.25rem;
  }

  /* The flexible column. See the note on `.fb`: `max-width: 0` is what stops
     the table sizing this column to the longest name and overflowing. */
  .c-name {
    width: 100%;
    max-width: 0;
  }

  .fb-cell {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
  }

  .fb-link {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
    color: inherit;
    text-decoration: none;
    font-weight: 500;
  }

  .fb-link :global(.icon) {
    color: var(--color-ink-soft, #6e665c);
  }

  .fb-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fb-link:hover .fb-text {
    color: var(--color-red-deep, #c01030);
  }

  .fb-dead,
  .fb-provisional {
    color: var(--color-ink-soft, #6e665c);
  }

  /* What this person may do with a document they do not own. It was a word in
     the old two-line metadata and it keeps its place on the row rather than
     costing a column of its own. */
  .fb-role {
    flex: 0 0 auto;
    font-size: 0.6875rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-ink-soft, #6e665c);
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.25rem;
    padding: 0 0.3rem;
  }

  /* The fixed columns: sized to their own content, never wrapped. The header
     and the cells share the width rule and nothing else. */
  .c-when,
  .c-rev,
  .c-by,
  .c-actions {
    width: 1%;
    white-space: nowrap;
  }

  /* Mono on the CELLS only -- a column of dates and a column of revision
     numbers are read down rather than along -- and not on the headers, which
     are one typeface across the row. */
  td.c-when,
  td.c-rev,
  td.c-by {
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: var(--color-ink-soft, #6e665c);
  }

  td.c-by {
    max-width: 9rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .c-actions :global(.row-actions) {
    justify-content: flex-end;
  }

  /* Inert rows at the height of a real one and in the same columns, so the
     answer landing replaces them without moving anything. */
  .fb-skel {
    display: block;
    height: 0.7rem;
    margin: 0;
  }

  /* Visible to a screen reader and nowhere else: the actions column's name. */
  .fb-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    border: 0;
  }

  .disclosure {
    display: inline-flex;
    align-items: center;
  }

  .disclosure.open :global(.icon-chevron) {
    rotate: 90deg;
  }


  .quiet {
    margin: 0.5rem 0 0;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  /* The empty folder's one row: centred across the whole table, quiet, and
     the height of an ordinary row so the list keeps its shape. */
  .fb-empty td {
    padding: 0.75rem 0.5rem;
    text-align: center;
    color: var(--color-ink-soft, #6e665c);
  }

  .empty.centred {
    text-align: center;
  }

  .flash-bad {
    border-color: var(--color-red-deep, #c01030);
  }

  /* The drag's own region. Quieter than the other two on purpose: it describes
     a gesture in progress rather than something that has happened, and it
     disappears the moment the gesture ends. */
  .flash-drag {
    border-style: dashed;
    color: var(--color-ink-soft, #6e665c);
  }

  /* Out of the list's flow, so the gesture cannot move what it is aimed at.
     Bottom centre, above the content, and transparent to the pointer. */
  .drag-toast {
    position: fixed;
    inset-inline: 0;
    bottom: 1.5rem;
    z-index: 30;
    width: max-content;
    max-width: min(90vw, 32rem);
    margin-inline: auto;
    padding: 0.5rem 0.875rem;
    border: 1px dashed var(--color-line, #211d1824);
    border-radius: 999px;
    background: var(--color-plinth, #f1ece4);
    box-shadow: 0 1px 12px rgb(33 29 24 / 12%);
    font-size: var(--text-meta, 0.8125rem);
    pointer-events: none;
  }

  /* ------------------------------------------------------- drag and drop
   *
   * The whole visual vocabulary, and it is deliberately three rules.
   *
   * `outline` rather than `border` on the target: a border changes the row's
   * box, and a table row that grows by 2px under the pointer shuffles every
   * row below it in the middle of a drag.
   *
   * A target that cannot take the drop is DASHED as well as red -- nothing on
   * this screen is conveyed by colour alone (§9) -- and the reason is said in
   * `dragNotice`, which is a `role="status"` region, so it is announced as
   * well as drawn.
   */
  /* One colour, two rules, drawn as four insets so a `border-collapse` table
     can have a ring round a row at all: top and bottom on every cell, and the
     two ends capped by the first and last cell. */
  .fb-row.drop-welcome td,
  .fb-row.drop-refuses td {
    box-shadow:
      inset 0 2px 0 var(--drop-ring),
      inset 0 -2px 0 var(--drop-ring);
  }

  .fb-row.drop-welcome td:first-child,
  .fb-row.drop-refuses td:first-child {
    box-shadow:
      inset 2px 0 0 var(--drop-ring),
      inset 0 2px 0 var(--drop-ring),
      inset 0 -2px 0 var(--drop-ring);
  }

  .fb-row.drop-welcome td:last-child,
  .fb-row.drop-refuses td:last-child {
    box-shadow:
      inset -2px 0 0 var(--drop-ring),
      inset 0 2px 0 var(--drop-ring),
      inset 0 -2px 0 var(--drop-ring);
  }

  .fb-row.drop-welcome {
    --drop-ring: var(--color-ink, #211d18);
  }

  .fb-row.drop-welcome td {
    background: var(--color-plinth, #f1ece4);
  }

  /*
   * Refusing, and the honest account of how it is signalled.
   *
   * A `box-shadow` ring cannot be dashed the way the tree's `outline` is, so
   * the two states differ here by ring colour, by the tint (welcome has one,
   * refusing does not) and by the cursor -- and, the part that actually
   * carries it for somebody who cannot see any of that, by `dragNotice`, which
   * is a `role="status"` region naming the obstacle in words. Colour is never
   * the only signal; it is not, on its own, a strong one either.
   */
  .fb-row.drop-refuses {
    --drop-ring: var(--color-red-deep, #c01030);
  }

  .fb-row.drop-refuses td {
    cursor: not-allowed;
  }

  /* The row being dragged: faded, so the person can see it is the thing in
     flight. Not animated -- there is nothing to animate, and the drag image is
     the browser's. */
  .fb-row.fb-dragging td {
    opacity: 0.45;
  }

  .crumb.drop-welcome {
    outline: 2px solid var(--color-ink, #211d18);
    outline-offset: 2px;
    border-radius: 0.25rem;
    color: var(--color-ink, #211d18);
  }

  .crumb.drop-refuses {
    outline: 2px dashed var(--color-red-deep, #c01030);
    outline-offset: 2px;
    border-radius: 0.25rem;
    cursor: not-allowed;
  }

  .fb-row.drop-welcome td,
  .fb-row.drop-refuses td,
  .fb-row.fb-dragging td,
  .crumb.drop-welcome,
  .crumb.drop-refuses {
    transition:
      background-color 0.12s ease,
      opacity 0.12s ease,
      box-shadow 0.12s ease,
      outline-color 0.12s ease;
  }

  /* The one animated thing in the gesture, and it is off when it is not
     wanted. The drag image itself is the browser's and carries no animation. */
  @media (prefers-reduced-motion: reduce) {
    .fb-row.drop-welcome td,
    .fb-row.drop-refuses td,
    .fb-row.fb-dragging td,
    .crumb.drop-welcome,
    .crumb.drop-refuses {
      transition: none;
    }
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
  }

  .field-label {
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  .field-input {
    padding: 0.5rem 0.6rem;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.375rem;
    background: var(--color-paper, #faf9f5);
    color: inherit;
    font: inherit;
  }

  .field-note {
    margin: 0;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
  }

  .choices {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    max-height: 14rem;
    overflow-y: auto;
  }

  .choice {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.4rem;
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .choice:hover {
    background: var(--color-plinth, #f1ece4);
  }
</style>
